import React, {useContext, useState} from 'react';
import {Box,
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Divider,
    Switch,
    FormControlLabel,
    Avatar,
    Paper,
    Alert} from "@mui/material";
import {PAContext} from "../../../Services/PAContext";
import {
    Description as FileIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    CalendarToday as CalendarIcon,
    Shield as ShieldIcon,
    WarningAmber as WarningIcon,
    LightbulbOutlined as HintIcon
} from '@mui/icons-material';


function Documents(){


    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;

    const [documents, setDocuments] = useState(Account.documents || []);

    const [viewingDoc, setViewingDoc] = useState(null);
    const [editingDoc, setEditingDoc] = useState(null);
    const [isPasscodeDialogOpen, setIsPasscodeDialogOpen] = useState(false);
    const [passcode, setPasscode] = useState('');
    const [passcodeError, setPasscodeError] = useState(false);
    const [pendingViewDoc, setPendingViewDoc] = useState(null);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    // Form State for Adding/Editing
    const [formState, setFormState] = useState({
        name: '',
        notes: '',
        expires: '',
        passcode_needed: true,
        private: true,
        encrypted: true
    });

    const handleRequestView = (doc) => {
        if (doc.passcode_needed) {
            setPendingViewDoc(doc);
            setPasscode('');
            setPasscodeError(false);
            setIsPasscodeDialogOpen(true);
        } else {
            setViewingDoc(doc);
        }
    };

    const handleVerifyPasscode = () => {
        // Validation check for the default passcode
        if (passcode === '1234') {
            setViewingDoc(pendingViewDoc);
            setIsPasscodeDialogOpen(false);
            setPendingViewDoc(null);
            setPasscodeError(false);
        } else {
            setPasscodeError(true);
            // Informative log for development, but UI now provides feedback directly
            console.error("Incorrect passcode. (Try '1234')");
        }
    };

    const handleEditClick = (doc) => {
        setEditingDoc(doc);
        setFormState({
            name: doc.name,
            notes: doc.notes,
            expires: doc.expires,
            passcode_needed: doc.passcode_needed,
            private: doc.private,
            encrypted: doc.encrypted
        });
    };


    const handleSaveEdit = () => {
        if (editingDoc) {
            setDocuments(docs => docs.map(d =>
                d.id === editingDoc.id ? { ...d, ...formState } : d
            ));
        } else {
            const newDoc = {
                id: Date.now(),
                ...formState,
                created: Date.now(),
                last_accessed: 0,
                file_name: "upload_temp.jpg",
                file_size: "0 MB",
                file_type: "image/jpeg",
                status: 1
            };
            setDocuments(prev => [...prev, newDoc]);
        }
        setEditingDoc(null);
        setIsAddDialogOpen(false);
    };

    const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString() : 'Never';

    function handleSaveAccountDocuments() {
        // In a real app, you'd POST the `essentials` array to your API here
        console.log("Saving Documents:");

    }

    return (
        <Box>
            {/* SAVE Button */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setFormState({ name: '', notes: '', expires: '', passcode_needed: true, private: true, encrypted: true });
                        setIsAddDialogOpen(true);
                    }}
                    sx={{ bgcolor: '#f09637', '&:hover': { bgcolor: '#d9822b' } }}
                >
                    Add New
                </Button>
                <Button
                    variant="contained"
                    startIcon={<i className="icon-floppy-disk" />}
                    onClick={handleSaveAccountDocuments}
                    sx={{ bgcolor: '#f09637', '&:hover': { bgcolor: '#d9822b' } }}
                >
                    Save Documents
                </Button>
            </Box>

            <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a' }}>Secure Documents</Typography>
                <Typography variant="body1" color="text.secondary">Manage and view your encrypted files.</Typography>
            </Box>


            {/* Document Grid */}
            <Grid container spacing={4}>
                {documents.map((doc) => (
                    <Grid item xs={12} sm={6} md={4} key={doc.id}>
                        <Card sx={{
                            borderRadius: '24px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            border: '1px solid #f0f0f0',
                            '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                            }
                        }}>
                            {/* Card Body */}
                            <Box sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 2, textAlign: 'center' }}>
                                    {doc.name}
                                </Typography>

                                {/* Image Preview Area */}
                                <Box
                                    onClick={() => handleRequestView(doc)}
                                    sx={{
                                        height: '180px',
                                        bgcolor: doc.encrypted ? '#fff7ed' : '#f0f9ff',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        mb: 3,
                                        border: '2px dashed',
                                        borderColor: doc.encrypted ? '#f09637' : '#0284c7',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: 'white', color: doc.encrypted ? '#f09637' : '#0284c7', width: 56, height: 56, mb: 1, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                                        {doc.passcode_needed ? <LockIcon /> : <FileIcon />}
                                    </Avatar>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: doc.encrypted ? '#f09637' : '#0284c7' }}>
                                        {doc.file_type.split('/')[1].toUpperCase()} • {doc.file_size}
                                    </Typography>
                                    {doc.passcode_needed && (
                                        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                                            <ShieldIcon sx={{ color: '#f09637', fontSize: 20 }} />
                                        </Box>
                                    )}
                                </Box>

                                {/* Meta Data */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CalendarIcon sx={{ fontSize: 18, color: '#f09637' }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Created</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(doc.created)}</Typography>
                                        </Box>
                                    </Box>

                                    {doc.expires && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <WarningIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Expires</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>{doc.expires}</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            <Divider />

                            {/* Action Bottom Bar */}
                            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fafafa' }}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Encrypted">
                                        <IconButton size="small"><ShieldIcon sx={{ fontSize: 18, color: doc.encrypted ? '#10b981' : '#ccc' }} /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Private">
                                        <IconButton size="small"><LockIcon sx={{ fontSize: 18, color: doc.private ? '#6366f1' : '#ccc' }} /></IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                        onClick={(e) => handleEditClick(doc)}
                                        sx={{ bgcolor: 'white', border: '1px solid #eee', '&:hover': { bgcolor: '#f0f9ff', color: '#0284c7' } }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setDocuments(docs => docs.filter(d => d.id !== doc.id))}
                                        sx={{ bgcolor: 'white', border: '1px solid #eee', '&:hover': { bgcolor: '#fff1f2', color: '#e11d48' } }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>




            {/* Passcode Dialog */}
            <Dialog
                open={isPasscodeDialogOpen}
                onClose={() => setIsPasscodeDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '24px', p: 2, maxWidth: '400px' } }}
            >
                <DialogTitle component="div" sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: '#fff7ed', color: '#f09637', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <LockIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Secret Passcode</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary', fontWeight: 500 }}>
                        The file <strong>{pendingViewDoc?.name}</strong> is locked. <br/> Enter the 4-digit key to unlock.
                    </Typography>

                    {/* Error Feedback in UI */}
                    {passcodeError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', border: 'none' }}>
                            Incorrect passcode. Please try again.
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        type="password"
                        placeholder="••••"
                        autoFocus
                        error={passcodeError}
                        value={passcode}
                        onChange={(e) => {
                            setPasscode(e.target.value);
                            if (passcodeError) setPasscodeError(false);
                        }}
                        inputProps={{
                            style: { letterSpacing: '12px', fontSize: '24px', textAlign: 'center', fontWeight: 900 }
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleVerifyPasscode()}
                    />

                    {/* Visible Hint for User */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, opacity: 0.7 }}>
                        <HintIcon sx={{ fontSize: 16, color: '#f09637' }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Hint: The default passcode is 1234
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleVerifyPasscode}
                        sx={{ bgcolor: '#1a1a1a', py: 1.5, borderRadius: '12px', fontWeight: 700, '&:hover': { bgcolor: '#333' } }}
                    >
                        Unlock Now
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog
                fullWidth
                maxWidth="md"
                open={Boolean(viewingDoc)}
                onClose={() => setViewingDoc(null)}
                PaperProps={{ sx: { borderRadius: '28px' } }}
            >
                <Box sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#f0f9ff', color: '#0284c7' }}><LockOpenIcon /></Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{viewingDoc?.name}</Typography>
                                <Typography variant="caption" color="text.secondary">Decrypted Session Active</Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setViewingDoc(null)}><DeleteIcon /></IconButton>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <Box sx={{
                                width: '100%',
                                height: '400px',
                                bgcolor: '#f8fafc',
                                borderRadius: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #f1f5f9'
                            }}>
                                <Typography sx={{ color: 'text.disabled', fontWeight: 600 }}>[ Image Data: {viewingDoc?.file_name} ]</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', bgcolor: '#fafafa' }}>
                                    <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary' }}>Metadata</Typography>
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Size</Typography>
                                            <Typography variant="body2" fontWeight={700}>{viewingDoc?.file_size}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Type</Typography>
                                            <Typography variant="body2" fontWeight={700}>{viewingDoc?.file_type}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Last View</Typography>
                                            <Typography variant="body2" fontWeight={700}>{formatDate(viewingDoc?.last_accessed)}</Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Notes</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                        {viewingDoc?.notes || 'No extra information provided for this document.'}
                                    </Typography>
                                </Box>

                                <Button
                                    variant="outlined"
                                    color="error"
                                    fullWidth
                                    onClick={() => setViewingDoc(null)}
                                    sx={{ mt: 'auto', borderRadius: '12px', fontWeight: 700 }}
                                >
                                    Close Secure View
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>

            {/* Form Dialog */}
            <Dialog
                open={Boolean(editingDoc) || isAddDialogOpen}
                onClose={() => {setEditingDoc(null); setIsAddDialogOpen(false);}}
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ fontWeight: 900, px: 4, pt: 4 }}>
                    {editingDoc ? 'Edit Document' : 'New Document'}
                </DialogTitle>
                <DialogContent sx={{ px: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1, width: '380px' }}>
                        <TextField
                            label="Friendly Name"
                            fullWidth
                            variant="filled"
                            value={formState.name}
                            onChange={(e) => setFormState({...formState, name: e.target.value})}
                        />
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            variant="filled"
                            value={formState.notes}
                            onChange={(e) => setFormState({...formState, notes: e.target.value})}
                        />
                        <TextField
                            label="Expiration Date"
                            fullWidth
                            placeholder="MM/DD/YYYY"
                            variant="filled"
                            value={formState.expires}
                            onChange={(e) => setFormState({...formState, expires: e.target.value})}
                        />

                        <Divider />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                control={<Switch checked={formState.passcode_needed} onChange={(e) => setFormState({...formState, passcode_needed: e.target.checked})} color="warning" />}
                                label={<Typography variant="body2" fontWeight={600}>Passcode Protection</Typography>}
                            />
                            <FormControlLabel
                                control={<Switch checked={formState.private} onChange={(e) => setFormState({...formState, private: e.target.checked})} />}
                                label={<Typography variant="body2" fontWeight={600}>Private Visibility</Typography>}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 4 }}>
                    <Button onClick={() => {setEditingDoc(null); setIsAddDialogOpen(false);}} sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#f09637', borderRadius: '12px', fontWeight: 800, px: 3 }}
                        onClick={handleSaveEdit}
                    >
                        {editingDoc ? 'Update Document' : 'Create Entry'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>

    )

}

export default Documents;